import cn from 'classnames';
import ruLocale from 'date-fns/locale/ru';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ButtonAdd } from '@features/events/common';
import { Calendar, Close } from '@shared/assets';
import { Card, IconButton } from '@shared/ui';
import { getErrorMessages } from '@src/library/helpers/events/errorMessages';
import { TimePicker } from 'library/components/common';
import { useEventEditorTime } from 'library/hooks/events';
import { formatHours, getDuration } from './lib';
import * as st from './styles.module.scss';
import { Duration } from './ui/duration';

type Props = {
  hasTickets: boolean;
};

export const DateTime = ({ hasTickets }: Props): JSX.Element => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const { setDates, timeFromName, timeFromDayName, timeToName } = useEventEditorTime();
  const {
    fields: dayFields,
    append: appendDay,
    remove: removeDay,
  } = useFieldArray({
    name: 'timetable',
    control,
  });

  const timetable = watch('timetable');

  const errorMessages = getErrorMessages(errors, ['timetable']);
  const errorMessage = errorMessages.length === 3 ? 'Данные не заполнены' : errorMessages.join(' ');

  return (
    <fieldset className={st.datetime}>
      <div className={st.datetime__header}>
        <h3>Дата и время</h3>
        <p>{errorMessage}</p>
      </div>

      <div className={st.datetime__add}>
        <ButtonAdd
          handler={() =>
            appendDay({
              time_from_day: new Date(),
              time_from: new Date(),
              time_to: new Date(),
            })
          }
          disabled={!!errors.timetable}
        />
      </div>
      <div className={st.blocks}>
        {dayFields.map((field, index, arr) => {
          const timeFrom = timetable[index].time_from;
          const timeTo = timetable[index].time_to;
          const duration = getDuration(timeFrom, timeTo) <= 0 ? 0 : getDuration(timeFrom, timeTo);

          return (
            <Card className={st.date} key={field.id}>
              <div className={st.date__wrap}>
                <Controller
                  control={control}
                  name={timeFromDayName(index)}
                  render={({ field: { value, onChange } }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ruLocale}>
                      <div className={cn(st.date__date, arr.length === 1 && st.fullsize)}>
                        <MuiDatePicker
                          label="Дата"
                          value={value}
                          format="dd.MM.yyyy"
                          disabled={hasTickets}
                          onChange={(newValue) => {
                            setDates(index, newValue);
                            onChange(newValue);
                          }}
                          slots={{ openPickerIcon: Calendar }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              placeholder: 'дд.мм.гггг',
                            },
                          }}
                        />
                      </div>
                    </LocalizationProvider>
                  )}
                />
                <div className={st.date__start}>
                  <TimePicker
                    control={control}
                    label="Начало"
                    index={index}
                    disabled={hasTickets}
                    name={timeFromName(index)}
                  />
                </div>
                <div className={st.date__end}>
                  <TimePicker
                    control={control}
                    label="Конец"
                    index={index}
                    disabled={hasTickets}
                    name={timeToName(index)}
                  />
                </div>
              </div>
              <div className={st.date__duration}>
                <Duration duration={formatHours(duration)} />
              </div>
              {arr.length > 1 && !hasTickets && (
                <div className={st.date__close}>
                  <IconButton variant="transparent" onClick={() => removeDay(index)}>
                    <Close />
                  </IconButton>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </fieldset>
  );
};
